/**
 * Shared platform info for FontAwesome brand icons.
 *
 * Single canonical source used by all components that need
 * platform colors, icons, and display names.
 *
 * Keys are UPPERCASE to match the platform enum used in the DB
 * and existing consumers (ChannelSelect, ChannelMultiSelect, BrandCheck, etc.).
 */

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';

export interface PlatformInfo {
  /** Brand hex color */
  color: string;
  /** FontAwesome icon definition */
  icon: IconDefinition;
  /** Human-readable display name */
  name: string;
}

export const PLATFORM_INFO: Record<string, PlatformInfo> = {
  LINKEDIN:           { color: '#0A66C2', icon: faLinkedin,  name: 'LinkedIn'  },
  FACEBOOK:           { color: '#1877F2', icon: faFacebook,  name: 'Facebook'  },
  INSTAGRAM:          { color: '#E4405F', icon: faInstagram, name: 'Instagram' },
  TWITTER:            { color: '#000000', icon: faXTwitter,  name: 'X'         },
  YOUTUBE:            { color: '#FF0000', icon: faYoutube,   name: 'YouTube'   },
  TIKTOK:             { color: '#000000', icon: faTiktok,    name: 'TikTok'    },
  PINTEREST:          { color: '#E60023', icon: faPinterest, name: 'Pinterest' },
  THREADS:            { color: '#000000', icon: faThreads,   name: 'Threads'   },
  GOOGLE_MY_BUSINESS: { color: '#4285F4', icon: faGoogle,    name: 'Google'    },
  BLUESKY:            { color: '#0085FF', icon: faBluesky,   name: 'Bluesky'   },
  OTHER:              { color: '#6B7280', icon: faGlobe,     name: 'Other'     },
};

/**
 * Returns platform info for a given platform string.
 * Falls back to a globe icon with the raw platform string as the name
 * if the platform is not in PLATFORM_INFO.
 */
export const getPlatformInfo = (platform: string): PlatformInfo =>
  PLATFORM_INFO[platform.toUpperCase()] ??
  PLATFORM_INFO['OTHER'] ??
  { color: '#6B7280', icon: faGlobe, name: platform };

/**
 * Single source of truth for channels that are NOT valid SOCIAL-post
 * targets — asset/reference channels (Canva designs, Google Drive files,
 * WordPress blog posts) that must never appear as a selectable target when
 * composing/scheduling a social post, nor in social-only channel/goal/
 * analytics filters.
 *
 * NOTE: TWITTER is a separate "discontinued platform" exclusion used in some
 * contexts (see `feedback_twitter_discontinued`) — it is intentionally NOT
 * folded into this list. Contexts that need to exclude both should combine
 * `NON_SOCIAL_CHANNEL_PLATFORMS` with their own TWITTER exclusion.
 */
export const NON_SOCIAL_CHANNEL_PLATFORMS = ['CANVA', 'GDRIVE', 'WORDPRESS'] as const;

export type NonSocialChannelPlatform = (typeof NON_SOCIAL_CHANNEL_PLATFORMS)[number];

/**
 * Returns true when `platform` is a valid target for a SOCIAL post, i.e. it
 * is not an asset/blog-only channel (Canva, Google Drive, WordPress).
 */
export const isSocialPublishTarget = (platform: string): boolean =>
  !(NON_SOCIAL_CHANNEL_PLATFORMS as readonly string[]).includes(platform);
